import { useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth/lib/types';
import { signup } from 'services/signup';
import { maskPhone } from 'helpers/masks';
import { validateEmail, checkPassword, validateName } from 'helpers/validation';
import { toast } from 'react-toastify';
import { Footer, Input, Header, Select } from 'components';
import { SmallBtn } from 'components/Buttons';

import {
  SignupContainer,
  FormContainer,
  SocialMediaLinks,
  SocialMedia,
  SignupMainButton,
} from './style';

import LogoRegenesis from 'assets/logo/logo-regenesis-final.svg';
import Seta from 'assets/icon/seta.svg';
import smoothscroll from 'smoothscroll-polyfill';
import FacebookButton from 'components/FacebookButton';
import GoogleButton from 'components/GoogleButton';

smoothscroll.polyfill();

interface props extends RouteComponentProps {}

function Signup({ history }: props) {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [repeatPassword, setRepeatPassword] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [quarter, setQuarter] = useState<string>('');
  const [coupon, setCoupon] = useState<string>('');
  const [knowabout, setKnowabout] = useState<string>('');

  const [emailError, setEmailError] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  // const [phoneError, setPhoneError] = useState<string>('');
  const [repeatPasswordError, setRepeatPasswordError] = useState<string>('');
  const [nameError, setNameError] = useState<string>('');
  const [quarterError, setQuarterError] = useState<string>('');
  const [couponError, setCouponError] = useState<string>('');
  const [knowaboutError, setKnowaboutError] = useState<string>('');

  const getErrorMessage = async (error: any): Promise<void> => {
    return error && error.response && error.response.data
      ? typeof error.response.data.message === 'object'
        ? error.response.data.message.message
        : error.response.data.message
      : error.message || error;
  };

  const handleBlurCoupon = (): void => {
    setCoupon(coupon.trim().toUpperCase());
  };

  const handleSignup = async (): Promise<void> => {
    if (!name) {
      setNameError('Preencha corretamente este campo');
      return;
    }

    if (!email) {
      setEmailError('Preencha corretamente este campo');
      return;
    }

    // if (!phone) {
    //   setPhoneError('Preencha corretamente este campo');
    //   return;
    // }

    if (!quarter) {
      setQuarterError('Preencha corretamente este campo');
      return;
    }

    if (!knowabout) {
      setKnowaboutError('Preencha corretamente este campo');
      return;
    }

    if (!coupon) {
      setCouponError('Preencha corretamente este campo');
      return;
    }

    if (!password) {
      setPasswordError('Preencha corretamente este campo');
      return;
    }

    if (!repeatPassword) {
      setRepeatPasswordError('Preencha corretamente este campo');
      return;
    }

    if (validateName(name) || name.length < 2) {
      setNameError(
        'O nome precisa ter pelo menos duas letras e nenhum caracter especial.',
      );
      return;
    }

    if (password !== repeatPassword) {
      setPasswordError('As senhas não se correspondem, informe senhas iguais.');
      setRepeatPasswordError(
        'As senhas não se correspondem, informe senhas iguais.',
      );
      return;
    }

    if (password === repeatPassword && !checkPassword(password)) {
      setPasswordError(
        'A senha precisa conter no mínimo 8 caracteres, incluindo letras maíusculas, minúsculas e números',
      );
      return;
    }

    // if (
    //   phone.replace('(', '').replace(')', '').replace('-', '').replace(' ', '')
    //     .length < 11
    // ) {
    //   setPhoneError('O número de telefone está inválido.');
    //   return;
    // }

    if (!validateEmail(email) && email) {
      setEmailError('Informe um e-mail com formato válido');
      return;
    }

    if (
      !emailError &&
      !nameError &&
      !passwordError &&
      !repeatPasswordError &&
      // !phoneError &&
      !knowaboutError &&
      !quarterError &&
      !couponError
    ) {
      const data = {
        email,
        password,
        name,
        quarter,
        coupon,
        knowabout,
        telephone: phone
          .replace('(', '')
          .replace(')', '')
          .replace('-', '')
          .replace(' ', ''),
      };

      signup(data)
        .then(response => {
          if (response) {
            history.push('/redefinir', {
              email,
              type: 'signup',
            });
            toast.success(
              'Cadastro criado! Verifique seu email para receber um código de segurança',
            );
          }
        })
        .catch(error => {
          if (error.response.data.message) {
            toast.error('⚠ ' + error.response.data.message);
            return;
          }

          toast.error(
            '⚠ Algo deu errado ao concluir seu cadastro. Tente novamente.',
          );
        });
    }
  };

  const handleChangeInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ): void => {
    switch (e.target.id) {
      case 'name-input':
        setName(e.target.value);
        break;

      case 'email-input':
        setEmail(e.target.value);
        break;

      case 'phone-input':
        setPhone(maskPhone(e.target.value));
        break;

      case 'password-input':
        setPassword(e.target.value);
        break;

      case 'repeat-password-input':
        setRepeatPassword(e.target.value);
        break;

      case 'quarter-select':
        setQuarter(e.target.value);
        break;

      case 'knowabout-select':
        setKnowabout(e.target.value);
        break;

      case 'coupon-input':
        setCoupon(e.target.value);
        break;

      default:
        break;
    }
  };

  return (
    <SignupContainer>
      <Header reduceHeader={false} />

      <FormContainer>
        <div className="logo-wrapper">
          <img src={LogoRegenesis} alt="regenesis" />
        </div>
        <div className="title">
          <h2>Cadastre-se com seu e-mail</h2>

          {/* <div className="btn-signup-xl">
            <SocialMediaLinks>
              <SocialMedia
                onClick={() =>
                  Auth.federatedSignIn({
                    provider: CognitoHostedUIIdentityProvider.Facebook,
                  })
                }
                onKeyDown={e =>
                  e.code === 'Enter' &&
                  Auth.federatedSignIn({
                    provider: CognitoHostedUIIdentityProvider.Facebook,
                  })
                }
                tabIndex={0}
                aria-label="Entre com o Facebook"
              >
                <FacebookButton aria-hidden />
              </SocialMedia>
              <SocialMedia
                onClick={() =>
                  Auth.federatedSignIn({
                    provider: CognitoHostedUIIdentityProvider.Google,
                  })
                }
                onKeyDown={e =>
                  e.code === 'Enter' &&
                  Auth.federatedSignIn({
                    provider: CognitoHostedUIIdentityProvider.Google,
                  })
                }
                tabIndex={0}
                aria-label="Entre com o Google"
              >
                <GoogleButton aria-hidden />
              </SocialMedia>
            </SocialMediaLinks>
          </div> */}

          {/* <div className="btn-signup-sm">
            <SocialMediaLinks>
              <SocialMedia
                onClick={() =>
                  Auth.federatedSignIn({
                    provider: CognitoHostedUIIdentityProvider.Facebook,
                  })
                }
                onKeyDown={e =>
                  e.code === 'Enter' &&
                  Auth.federatedSignIn({
                    provider: CognitoHostedUIIdentityProvider.Facebook,
                  })
                }
                tabIndex={0}
                aria-label="Entre com o Facebook"
              >
                <FacebookButton aria-hidden />
                <p>Continuar com o Facebook</p>
              </SocialMedia>
              <SocialMedia
                onClick={() =>
                  Auth.federatedSignIn({
                    provider: CognitoHostedUIIdentityProvider.Google,
                  })
                }
                onKeyDown={e =>
                  e.code === 'Enter' &&
                  Auth.federatedSignIn({
                    provider: CognitoHostedUIIdentityProvider.Google,
                  })
                }
                tabIndex={0}
                aria-label="Entre com o Google"
              >
                <GoogleButton aria-hidden />
                <p>Continuar com o Google</p>
              </SocialMedia>
            </SocialMediaLinks>
          </div>

          <h4>ou com seu email</h4> */}
        </div>

        <Input
          type="text"
          id="name-input"
          value={name}
          onChange={handleChangeInput}
          onFocus={() => setNameError('')}
          label="Nome"
          placeholder="Seu nome completo"
        >
          {nameError && <span>{nameError}</span>}
        </Input>

        <Input
          type="email"
          id="email-input"
          value={email}
          onChange={handleChangeInput}
          onFocus={() => setEmailError('')}
          label="E-mail"
          placeholder="seu.email@exemplo.com"
        >
          {emailError && <span>{emailError}</span>}
        </Input>

        <div className="container-input-phone">
          <Input
            type="text"
            id="phone-input"
            value={phone}
            onChange={handleChangeInput}
            label="Celular/WhatsApp"
            placeholder="(11) 99999-9999"
          >
            {/* {phoneError && <span>{phoneError}</span>} */}
          </Input>
        </div>

        <div className="container-select-quarter">
          <Select
            id="quarter-select"
            label="Em que fase você está"
            onChange={handleChangeInput}
            onFocus={() => setQuarterError('')}
            errorMessage={quarterError && <span>{quarterError}</span>}
          >
            <option value="">Selecione o trimestre</option>
            <option value="quarter_1">1º Trimestre</option>
            <option value="quarter_2">2º Trimestre</option>
            <option value="quarter_3">3º Trimestre</option>
            <option value="breast-feeding">Amamentando</option>
          </Select>
        </div>

        <div className="container-select-knowabout">
          <Select
            id="knowabout-select"
            label="Onde você ficou sabendo do Curso de Gestante Regenesis"
            onChange={handleChangeInput}
            onFocus={() => setKnowaboutError('')}
            errorMessage={knowaboutError && <span>{knowaboutError}</span>}
          >
            <option value="">Escolha uma opção</option>
            <option value="option_1">Consulta com médico(a)</option>
            <option value="option_2">Redes Sociais de Regenesis</option>
            <option value="option_3">Indicação de influenciadora</option>
            <option value="option_4">QR Code na caixa de Regenesis</option>
            <option value="option_5">QR Code na farmácia</option>
            <option value="option_6">Site Mulher e Gestação</option>
            <option value="option_7">Indicação de amigos/familiares</option>
            <option value="option_8">Google / ChatGPT</option>
            <option value="option_9">Indicação farmácia</option>
            <option value="option_10">Benefício empresa</option>
            <option value="option_11">Outro</option>
          </Select>
        </div>

        <Input
          type="text"
          id="coupon-input"
          label="Cupom de acesso"
          value={coupon}
          onChange={handleChangeInput}
          onFocus={() => setCouponError('')}
          onBlur={handleBlurCoupon}
          alert="Caso não tenha, solicite ao seu médico(a) o cupom de acesso ao Curso de Gestante Regenesis."
        >
          {couponError && <span>{couponError}</span>}
        </Input>

        <Input
          type="password"
          id="password-input"
          value={password}
          onChange={handleChangeInput}
          onFocus={() => setPasswordError('')}
          label="Senha"
          alert="A senha precisa conter no mínimo 8 caracteres, incluindo letras maíusculas, minúsculas, números e caracteres especiais"
        >
          {passwordError && <span>{passwordError}</span>}
        </Input>

        <Input
          type="password"
          id="repeat-password-input"
          value={repeatPassword}
          onChange={handleChangeInput}
          onFocus={() => setRepeatPasswordError('')}
          label="Confirmar senha"
        >
          {repeatPasswordError && <span>{repeatPasswordError}</span>}
        </Input>

        <SignupMainButton onClick={handleSignup}>
          Cadastrar <img className="icon" src={Seta} alt="Arrow" />
        </SignupMainButton>

        <div className="form-footer">
          <p
            onClick={() =>
              history.push('/redefinir', {
                type: 'codeFirstStep',
              })
            }
          >
            Já tem um código de verificação ou não recebeu o código?
          </p>
          <p onClick={() => history.push('/login')}>Já tem uma conta?</p>
        </div>
      </FormContainer>

      <div className="footer-signup">
        <Footer
          backgroundColor={'var(--color-secondary-background-color)'}
          hasLogo
        />
      </div>
    </SignupContainer>
  );
}

export default Signup;
